'use client';

import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useSyncExternalStore,
} from 'react';
import type { ReportWithDetails } from '../../../types/shared';

// Типы событий
export type TrafficRealtimeEvent =
  | { type: 'NEW_REPORT'; payload: ReportWithDetails }
  | { type: 'DELETE_REPORT'; payload: { reportId: string } }
  | { type: 'REPORTS_CLEANED'; payload: { deletedIds: string[] } }
  | { type: 'VOTE_UPDATE'; payload: { reportId: string; likes: number; dislikes: number } }
  | {
      type: 'NEW_COMMENT';
      payload: {
        reportId: string;
        id: string;
        text: string;
        userId: string;
        createdAt: string;
        parentId: string | null;
        user: { id: string; name: string | null; image: string | null };
      };
    }
  | { type: 'DELETE_COMMENT'; payload: { reportId: string; commentId: string } }
  | {
      type: 'TRAFFIC_LEVEL';
      payload: { level: string | number | null; hint: string | null; time?: string };
    };

    type Listener = () => void;
    type GlobalListener = (event: TrafficRealtimeEvent) => void;

function getPayloadId(e: TrafficRealtimeEvent): string | undefined {
  if ('reportId' in e.payload) return e.payload.reportId;
  if (e.type === 'NEW_REPORT') return e.payload.id;
  return undefined;
}

// ───────────────── Store ─────────────────

const store = (() => {
  let isConnected = false;

  const itemListeners = new Map<string, Set<Listener>>();
  const globalListeners = new Set<GlobalListener>();
  const lastByItem = new Map<string, TrafficRealtimeEvent>();
  const connListeners = new Set<Listener>();

  const setConnected = (v: boolean) => {
    if (isConnected === v) return;
    isConnected = v;
    connListeners.forEach((l) => l());
  };

  const subscribeConn = (listener: Listener): (() => void) => {
    connListeners.add(listener);
    return () => {
      connListeners.delete(listener); // ✅ void
    };
  };

  const getConnSnapshot = () => isConnected;

  const subscribeItem = (id: string, listener: Listener) => {
    let set = itemListeners.get(id);
    if (!set) {
      set = new Set<Listener>();
      itemListeners.set(id, set);
    }
    set.add(listener);

    return () => {
      const s = itemListeners.get(id);
      if (!s) return;
      s.delete(listener);
      if (s.size === 0) itemListeners.delete(id);
    };
  };

  const subscribeGlobal = (listener: GlobalListener): (() => void) => {
    globalListeners.add(listener);
    return () => {
      globalListeners.delete(listener); // ✅ void
    };
  };

  const getItemSnapshot = (id: string) => lastByItem.get(id) ?? null;

  const publish = (event: TrafficRealtimeEvent) => {
    // ✅ чистим память по удаленным
    if (event.type === 'DELETE_REPORT') {
      lastByItem.delete(event.payload.reportId);
      itemListeners.delete(event.payload.reportId);
    }
    if (event.type === 'REPORTS_CLEANED') {
      for (const id of event.payload.deletedIds) {
        lastByItem.delete(id);
        itemListeners.delete(id);
      }
    }

    const id = getPayloadId(event);
    if (id) {
      lastByItem.set(id, event);
      const set = itemListeners.get(id);
      set?.forEach((l) => l());
    }

    globalListeners.forEach((l) => l(event));
  };

  return {
    setConnected,
    subscribeConn,
    getConnSnapshot,
    subscribeItem,
    subscribeGlobal,
    getItemSnapshot,
    publish,
  };
})();

const TrafficGlobalContext = createContext<{ _ready: true } | null>(null);
const READY = { _ready: true } as const;
export function TrafficGlobalEventsProvider({ children }: { children: ReactNode }) {
  const esRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
 

  useEffect(() => {
    let isActive = true;

    const connect = () => {
      if (!isActive || esRef.current?.readyState === 1) return;

      const es = new EventSource('/api/traffic/events');
      if (typeof window !== 'undefined') {
        (window as Window & { __trafficEventSource?: EventSource }).__trafficEventSource = es;
      }
      esRef.current = es;

      es.onopen = () => {
        reconnectAttemptsRef.current = 0;
        store.setConnected(true);
      };

      es.onmessage = (event) => {
        try {
          if (!event.data || event.data === ': ping') return;
          const parsed = JSON.parse(event.data) as TrafficRealtimeEvent | {
            level?: string | number | null;
            hint?: string | null;
            time?: string;
          };
          const data: TrafficRealtimeEvent =
            'type' in parsed
              ? parsed
              : {
                  type: 'TRAFFIC_LEVEL',
                  payload: {
                    level: parsed.level ?? null,
                    hint: parsed.hint ?? null,
                    time: parsed.time,
                  },
                };
          store.publish(data);
        } catch (err) {
          console.error('Traffic SSE Parse Error:', err);
        }
      };

      es.onerror = () => {
        store.setConnected(false);
        es.close();
        esRef.current = null;
        if (!isActive || reconnectTimerRef.current) return;
        const delay = Math.min(15000, 1000 * 2 ** reconnectAttemptsRef.current);
        reconnectAttemptsRef.current += 1;
        reconnectTimerRef.current = setTimeout(() => {
          reconnectTimerRef.current = null;
          connect();
        }, delay);
      };
    };

    connect();

    return () => {
      isActive = false;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      const current = esRef.current;
      if (current) {
        current.close();
      }
      esRef.current = null;
      store.setConnected(false);
      if (typeof window !== 'undefined') {
        const shared = (window as Window & { __trafficEventSource?: EventSource })
          .__trafficEventSource;
        if (shared === current) {
          delete (window as Window & { __trafficEventSource?: EventSource }).__trafficEventSource;
        }
      }
    };
  }, []);

  return (
  <TrafficGlobalContext.Provider value={READY}>
    {children}
  </TrafficGlobalContext.Provider>
);
}



// Хук для карточек
export function useTrafficItemEvents(reportId: string) {
  const ctx = useContext(TrafficGlobalContext);
  if (!ctx) throw new Error('useTrafficItemEvents must be used within TrafficGlobalEventsProvider');

  return useSyncExternalStore(
    (cb) => store.subscribeItem(reportId, cb),
    () => store.getItemSnapshot(reportId),
    () => store.getItemSnapshot(reportId),
  );
}

// 🔥 Новый Хук для Карты (слушает всё)
export function useTrafficGlobalListener(callback: (event: TrafficRealtimeEvent) => void) {
  const ctx = useContext(TrafficGlobalContext);
  if (!ctx)
    throw new Error('useTrafficGlobalListener must be used within TrafficGlobalEventsProvider');

  useEffect(() => {
    return store.subscribeGlobal(callback);
  }, [callback]);
}

export function useTrafficEventsConnection() {
  const ctx = useContext(TrafficGlobalContext);
  if (!ctx) throw new Error('useTrafficEventsConnection must be used within TrafficGlobalEventsProvider');

  return useSyncExternalStore(
    store.subscribeConn,
    store.getConnSnapshot,
    store.getConnSnapshot,
  );
}
